import { useState, useEffect } from 'react'
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { getTodayDateOnlyMx, startOfLocalDayFromApi } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const ReconciliationForm = ({ show, onHide, reconciliation, onSuccess }) => {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    bank_account: '',
    reconciliation_date: getTodayDateOnlyMx(),
    statement_balance: '',
    formato_importacion: '',
    formato_bancario: ''
  })
  const [errors, setErrors] = useState({})
  const [bookBalance, setBookBalance] = useState(null)
  const [difference, setDifference] = useState(null)
  const [loadingBalance, setLoadingBalance] = useState(false)

  // Obtener cuentas bancarias
  const { data: bankAccounts } = useQuery(
    'bank-accounts-for-reconciliation',
    async () => {
      const response = await api.get('/financial/bank-accounts/?is_active=true')
      return response.data.results || response.data || []
    }
  )

  // Obtener formatos bancarios
  const { data: formatos } = useQuery(
    'bank-formats',
    async () => {
      const response = await api.get('/financial/formatos-bancarios/')
      return response.data.results || response.data || []
    }
  )

  // Calcular saldo en libros cuando cambia la cuenta o fecha
  useEffect(() => {
    if (formData.bank_account && formData.reconciliation_date) {
      setLoadingBalance(true)
      // El saldo se calcula automáticamente al crear la conciliación
      // Por ahora solo mostramos un placeholder
      setLoadingBalance(false)
    }
  }, [formData.bank_account, formData.reconciliation_date])

  // Calcular diferencia cuando cambia el saldo del estado de cuenta
  useEffect(() => {
    if (formData.statement_balance && bookBalance !== null) {
      const statement = parseFloat(formData.statement_balance) || 0
      const book = parseFloat(bookBalance) || 0
      setDifference(statement - book)
    } else {
      setDifference(null)
    }
  }, [formData.statement_balance, bookBalance])

  // El serializer manda las relaciones como id suelto (`bank_account: 6`), no como objeto
  // ni como `bank_account_id`. Leer solo esas dos formas dejaba el campo vacio al editar: el
  // select esta deshabilitado en edicion, asi que no habia forma de llenarlo y guardar moria
  // en "Debe seleccionar una cuenta bancaria". Es la razon de fondo de que la conciliacion
  // "no se pudiera modificar", y no se veia porque la pantalla nunca abria este modo.
  const idDeRelacion = (valor) => {
    if (valor === null || valor === undefined || valor === '') return ''
    return typeof valor === 'object' ? (valor.id ?? '') : valor
  }

  useEffect(() => {
    if (reconciliation) {
      setFormData({
        bank_account: idDeRelacion(reconciliation.bank_account) || idDeRelacion(reconciliation.bank_account_id),
        reconciliation_date: reconciliation.reconciliation_date || getTodayDateOnlyMx(),
        statement_balance: reconciliation.statement_balance || '',
        formato_importacion: reconciliation.formato_importacion || '',
        formato_bancario: idDeRelacion(reconciliation.formato_bancario) || idDeRelacion(reconciliation.formato_bancario_id)
      })
      setBookBalance(reconciliation.book_balance)
      setDifference(reconciliation.difference)
    } else {
      setFormData({
        bank_account: '',
        reconciliation_date: getTodayDateOnlyMx(),
        statement_balance: '',
        formato_importacion: '',
        formato_bancario: ''
      })
      setBookBalance(null)
      setDifference(null)
    }
    setErrors({})
  }, [reconciliation, show])

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/financial/bank-reconciliations/', data)
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success('Conciliación creada exitosamente')
        queryClient.invalidateQueries('bank-reconciliations')
        if (onSuccess) onSuccess(data)
        onHide()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Error al crear conciliación'
        toast.error(errorMsg)
        if (error.response?.data) {
          setErrors(error.response.data)
        }
      }
    }
  )

  const updateMutation = useMutation(
    async (data) => {
      const response = await api.put(`/financial/bank-reconciliations/${reconciliation.id}/`, data)
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success('Conciliación actualizada exitosamente')
        queryClient.invalidateQueries('bank-reconciliations')
        if (onSuccess) onSuccess(data)
        onHide()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Error al actualizar conciliación'
        toast.error(errorMsg)
        if (error.response?.data) {
          setErrors(error.response.data)
        }
      }
    }
  )

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validaciones
    const newErrors = {}
    if (!formData.bank_account) {
      newErrors.bank_account = 'Debe seleccionar una cuenta bancaria'
    }
    if (!formData.reconciliation_date) {
      newErrors.reconciliation_date = 'Debe seleccionar una fecha de conciliación'
    }
    if (!formData.statement_balance) {
      newErrors.statement_balance = 'Debe ingresar el saldo del estado de cuenta'
    } else {
      const balance = parseFloat(formData.statement_balance)
      if (isNaN(balance)) {
        newErrors.statement_balance = 'El saldo debe ser un número válido'
      }
    }

    // Validar que la fecha no sea futura
    const fechaConciliacion = startOfLocalDayFromApi(formData.reconciliation_date)
    const hoy = startOfLocalDayFromApi(getTodayDateOnlyMx())
    if (fechaConciliacion && hoy && fechaConciliacion > hoy) {
      newErrors.reconciliation_date = 'La fecha de conciliación no puede ser futura'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    const submitData = {
      bank_account: parseInt(formData.bank_account),
      reconciliation_date: formData.reconciliation_date,
      statement_balance: parseFloat(formData.statement_balance),
      formato_importacion: formData.formato_importacion || '',
      formato_bancario: formData.formato_bancario ? parseInt(formData.formato_bancario) : null
    }

    if (reconciliation) {
      updateMutation.mutate(submitData)
    } else {
      createMutation.mutate(submitData)
    }
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {reconciliation ? 'Editar Conciliación' : 'Nueva Conciliación Bancaria'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {createMutation.isError && (
            <Alert variant="danger">
              {createMutation.error?.response?.data?.error || 'Error al crear conciliación'}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cuenta Bancaria *</Form.Label>
                <Form.Select
                  name="bank_account"
                  value={formData.bank_account}
                  onChange={handleChange}
                  isInvalid={!!errors.bank_account}
                  disabled={!!reconciliation} // No permitir cambiar cuenta en edición
                >
                  <option value="">Seleccionar cuenta...</option>
                  {bankAccounts?.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {account.account_number} ({account.bank_name})
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.bank_account}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de Conciliación *</Form.Label>
                <Form.Control
                  type="date"
                  name="reconciliation_date"
                  value={formData.reconciliation_date}
                  onChange={handleChange}
                  isInvalid={!!errors.reconciliation_date}
                  max={getTodayDateOnlyMx()}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.reconciliation_date}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Saldo del Estado de Cuenta *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="statement_balance"
                  value={formData.statement_balance}
                  onChange={handleChange}
                  isInvalid={!!errors.statement_balance}
                  placeholder="0.00"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.statement_balance}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Saldo según el estado de cuenta del banco
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Saldo en Libros</Form.Label>
                {loadingBalance ? (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="me-2" />
                    <span className="text-muted">Calculando...</span>
                  </div>
                ) : (
                  <Form.Control
                    type="text"
                    value={bookBalance !== null ? `${formatCurrency(bookBalance || 0)}` : 'Se calculará al crear'}
                    readOnly
                    className="bg-light"
                  />
                )}
                <Form.Text className="text-muted">
                  Saldo calculado según registros del sistema
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {difference !== null && (
            <Row>
              <Col md={12}>
                <Alert variant={Math.abs(difference) < 0.01 ? 'success' : 'warning'}>
                  <strong>Diferencia:</strong>{' '}
                  {formatCurrency(Math.abs(difference))}
                  {difference > 0 ? ' (El estado de cuenta tiene más saldo)' : difference < 0 ? ' (El sistema tiene más saldo)' : ' (Saldos coinciden)'}
                </Alert>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Formato de Importación</Form.Label>
                <Form.Select
                  name="formato_importacion"
                  value={formData.formato_importacion}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar formato...</option>
                  <option value="CSV">CSV</option>
                  <option value="OFX">OFX</option>
                  <option value="XML">XML</option>
                  <option value="personalizado">Personalizado</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Formato Bancario</Form.Label>
                <Form.Select
                  name="formato_bancario"
                  value={formData.formato_bancario}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar formato bancario...</option>
                  {formatos?.filter(f => f.activo).map(formato => (
                    <option key={formato.id} value={formato.id}>
                      {formato.nombre} ({formato.formato})
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Opcional: Formato específico del banco para importación
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {reconciliation ? 'Actualizando...' : 'Creando...'}
              </>
            ) : (
              reconciliation ? 'Actualizar' : 'Crear Conciliación'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default ReconciliationForm
